/**
 * useStrategyAssets
 *
 * Fetches and organises all CMS assets for a single strategy program page.
 * Replaces the hardcoded chart objects (strandQCharts / merinCharts / dravonCharts)
 * and legacy hardcoded document mappings.
 *
 * Usage:
 *   const { latestCharts, chartHistory, documents, loading, error } =
 *     useStrategyAssets('strand-q');
 *
 * @param {string} strategy - CMS slug: 'strand-q' | 'merin' | 'dravon'
 *
 * Returns:
 * {
 *   loading: boolean,
 *   error: string | null,
 *
 *   // Latest charts — used by the existing 4-button chart UI
 *   latestCharts: {
 *     week: { performance: string|null, exposure: string|null },
 *     month: { performance: string|null, exposure: string|null }
 *   },
 *
 *   // Full chart history — used by ChartHistoryDrawer
 *   // chartHistory[period][type] = sorted array of chart entries
 *   chartHistory: {
 *     week:  { performance: [...], exposure: [...] },
 *     month: { performance: [...], exposure: [...] }
 *   },
 *
 *   // Documents — latest of each type
 *   documents: {
 *     factsheet:    { url, label, year, month } | null,
 *     description:  { url, label, year, month } | null,
 *     descriptionCover: { url, label, year, month } | null,
 *     allFactsheets:   [...],  // full history
 *     allDescriptions: [...]   // full history
 *   }
 * }
 */

import { useState, useEffect } from 'react';

import { getStrategyAllAssets } from '../services/cmsService';

const useStrategyAssets = strategy => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [latestCharts, setLatestCharts] = useState({
    week: { performance: null, exposure: null },
    month: { performance: null, exposure: null }
  });
  const [chartHistory, setChartHistory] = useState({
    week: { performance: [], exposure: [] },
    month: { performance: [], exposure: [] }
  });
  const [documents, setDocuments] = useState({
    factsheet: null,
    description: null,
    descriptionCover: null,
    allFactsheets: [],
    allDescriptions: []
  });

  useEffect(() => {
    if (!strategy) return;

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      try {
        const {
          weeklyCharts,
          monthlyCharts,
          factsheets,
          descriptions,
          descriptionCover
        } = await getStrategyAllAssets(strategy);

        if (cancelled) return;

        // ── Organise weekly charts by type ──────────────────────────────────
        const weekPerf = weeklyCharts.filter(c => c.type === 'performance');
        const weekExpo = weeklyCharts.filter(c => c.type === 'exposure');
        const monthPerf = monthlyCharts.filter(c => c.type === 'performance');
        const monthExpo = monthlyCharts.filter(c => c.type === 'exposure');

        setLatestCharts({
          week: {
            performance: weekPerf[0]?.url ?? null,
            exposure: weekExpo[0]?.url ?? null
          },
          month: {
            performance: monthPerf[0]?.url ?? null,
            exposure: monthExpo[0]?.url ?? null
          }
        });

        setChartHistory({
          week: { performance: weekPerf, exposure: weekExpo },
          month: { performance: monthPerf, exposure: monthExpo }
        });

        // ── Documents ────────────────────────────────────────────────────────
        setDocuments({
          factsheet: factsheets[0] ?? null,
          description: descriptions[0] ?? null,
          descriptionCover: descriptionCover ?? null,
          allFactsheets: factsheets,
          allDescriptions: descriptions
        });
      } catch (err) {
        if (!cancelled) {
          setError(err.message || 'Failed to load strategy assets');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [strategy]);

  return { loading, error, latestCharts, chartHistory, documents };
};

export default useStrategyAssets;
